/**
 * Procurement Demo — Nami's jobs on the replenishment pages.
 *
 * THROWAWAY DEMO CODE. Every job runs the same way: Nami first says exactly
 * what it will do, item by item, and what will not happen; the buyer agrees
 * ("Go ahead"); the page changes; Nami reports what changed, with links to the
 * records. The jobs:
 * - on the page, "Fulfill all open orders" sends every open-demand row's
 *   transfer or purchase order (rows go open → drafting → fulfilled, state in
 *   `fulfillStore`);
 * - on the page, topping up the carts that are short of a supplier minimum
 *   adds the buy-ahead line that clears it (the board store);
 * - on an item, the transfer (or the purchase, when no warehouse has spare
 *   stock) is explained against the other option, then sent.
 * Typing "reset" on either page puts every order back to open.
 */

import { getSite } from '@/demo/procurement/data/company';
import { fmtMoney } from '@/demo/procurement/data/format';
import { getSupplier } from '@/demo/procurement/data/suppliers';
import {
  APPROVAL_TIERS,
  cartsForMode,
  fmtQty,
  makeForwardBuyLine,
  resolveBoard,
  thresholdNudge,
  type ResolvedCart,
  type ThresholdNudge,
} from '@/demo/procurement/pages/buyer-board/buyer-board-logic';
import {
  addCartLine,
  getBuyerBoardState,
} from '@/demo/procurement/pages/buyer-board/buyer-board-store';
import { unitWord } from '@/pages/authenticated/procurement/demo/components/ItemInsights';
import {
  DEMAND_ITEMS,
  URGENCY_META,
  getItem,
} from '@/pages/authenticated/procurement/demo/data';
import type { DemandItem } from '@/pages/authenticated/procurement/demo/data';
import {
  getFulfillStatus,
  hideAlternatives,
  isResetCommand,
  resetFulfillStatuses,
  setFulfillStatus,
  showAlternative,
} from '@/pages/authenticated/procurement/demo/fulfillStore';

export const FULFILL_ALL_ORDERS = 'Fulfill all open orders';
export const TOP_UP_MINIMUMS = 'Top up the carts short of a minimum';
export const SHOW_TRANSFER_OPTION = 'Can we transfer instead?';
export const CREATE_TRANSFER_ORDER = 'Create the transfer order';

const BOARD_PATH = '/procurement/replenishment/board';

/** Time between one row starting and the next, and how long a row stays "fulfilling". */
const ROW_STAGGER_MS = 700;
const ROW_FIRST_MS = 300;
const ROW_FULFILLING_MS = 800;
const SETTLE_AFTER_LAST_MS = 500;

/** A job Nami proposes, waits on, then runs. */
export interface ReplenishmentJob {
  /** What Nami will do, item by item, and what will not happen; ends with the question. */
  plan: string;
  /** Shown on the page while the plan is read: a comparison, never a change. */
  preview?: () => void;
  /** Changes the page once the buyer agrees. */
  act: () => Promise<void> | void;
  /** What changed, with links to the records; said after `act`. */
  report: string;
}

export type ReplenishmentTurn =
  /** Nami answers; `apply` changes the page once the answer is read. */
  | { kind: 'reply'; narration: string; apply?: () => void }
  /** Nami says the plan and waits for the buyer. */
  | { kind: 'propose'; job: ReplenishmentJob }
  /** The buyer agreed: run the job, then report. */
  | { kind: 'run'; job: ReplenishmentJob };

const TOP_UP_REQUEST =
  /\btop.?up\b|\bfree.freight\b|\bminimums?\b|\bshort of\b/i;
const FULFILL_REQUEST =
  /\b(fulfil+|fill|process|handle|work through|clear)\b|\bopen orders?\b|\bqueue\b/i;
const TRANSFER_REQUEST =
  /\b(transfer|instead|alternativ\w*|options?|other ways?|another way|else|fill|source|move (it|them|stock))\b/i;
const GO_AHEAD =
  /^\s*(go ahead|go for it|yes|yep|yeah|sure|ok|okay|do it|please do|proceed|confirm(ed)?|approved?|send (it|them)|create (it|them)|go)\b/i;
const DECLINE =
  /^\s*(no|nope|not now|not yet|cancel|stop|hold (off|on)|wait|don'?t|do not|leave it|skip)\b/i;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

function days(n: number): string {
  return `${n} ${plural(n, 'day', 'days')}`;
}

/** "SO #48255 · Sunbelt Mechanical" reads as "order #48255 from Sunbelt Mechanical". */
function orderRef(sourceRef: string): string {
  const match = /^SO (#\S+) · (.+)$/.exec(sourceRef);
  return match ? `order ${match[1]} from ${match[2]}` : sourceRef;
}

/** "$36.00 each", "$0.62 per roll". */
function unitPrice(price: number, uom: string): string {
  const money = fmtMoney(price, { cents: true });
  if (uom === 'EA') return `${money} each`;
  return `${money} per ${unitWord(uom).replace(/s$/, '')}`;
}

function itemLink(item: DemandItem): string {
  return `[${item.sku}](${BOARD_PATH}/${item.id})`;
}

/** When the item is needed, as the row says it. */
function needText(item: DemandItem): string {
  return item.demandSource === 'sales-order'
    ? `${orderRef(item.sourceRef)} is due in ${days(item.needByDays)}`
    : `the forecast needs it in ${days(item.needByDays)}`;
}

function reply(narration: string, apply?: () => void): ReplenishmentTurn {
  return { kind: 'reply', narration, apply };
}

// --- Fulfill all open orders -----------------------------------------------------------

function openOrders(): DemandItem[] {
  return DEMAND_ITEMS.filter(
    (item) => getFulfillStatus(item.id) !== 'fulfilled',
  ).sort(
    (a, b) =>
      URGENCY_META[a.urgency].order - URGENCY_META[b.urgency].order ||
      a.needByDays - b.needByDays,
  );
}

async function fulfillRows(rows: DemandItem[]): Promise<void> {
  await Promise.all(
    rows.map(async (item, index) => {
      await sleep(index * ROW_STAGGER_MS + ROW_FIRST_MS);
      setFulfillStatus(item.id, 'fulfilling');
      await sleep(ROW_FULFILLING_MS);
      setFulfillStatus(
        item.id,
        'fulfilled',
        isTransfer(item) ? 'transfer' : 'buy',
      );
    }),
  );
  await sleep(SETTLE_AFTER_LAST_MS);
}

function isTransfer(item: DemandItem): boolean {
  return (
    item.recommended === 'transfer' && item.scenarios.transfer !== undefined
  );
}

/** One line of the plan: what moves or what is bought, and when it lands. */
function fulfillPlanLine(item: DemandItem): string {
  const units = unitWord(item.uom);
  const head = `**${item.sku}** ${item.name}, ${item.warehouse}:`;
  const { transfer, buy } = item.scenarios;
  if (isTransfer(item) && transfer) {
    return `${head} move ${transfer.transferUnits} ${units} from ${transfer.fromWarehouse} (${transfer.surplusUnits} on hand, ${transfer.overMaxUnits} over its maximum). Arrives in ${days(transfer.etaDays)}; ${needText(item)}. No purchase, no freight.`;
  }
  if (buy) {
    const late = buy.leadDays - item.needByDays;
    const timing =
      late > 0 && item.demandSource === 'forecast-deficit'
        ? `${days(late)} after the forecast needs it; no customer order is waiting on it`
        : needText(item);
    return `${head} buy ${buy.units} ${units} from ${buy.vendor} at ${unitPrice(buy.unitCost, item.uom)}, ${fmtMoney(buy.baseValue)}. Delivered in ${days(buy.leadDays)}; ${timing}.`;
  }
  return `${head} review the options on its page.`;
}

function fulfillReportLine(item: DemandItem): string {
  const units = unitWord(item.uom);
  const { transfer, buy } = item.scenarios;
  if (isTransfer(item) && transfer) {
    return `Transfer order to ${transfer.fromWarehouse}: ${transfer.transferUnits} ${units} of ${item.sku} to ${transfer.toWarehouse}, arriving in ${days(transfer.etaDays)} — ${itemLink(item)}`;
  }
  if (buy) {
    return `Purchase order to ${buy.vendor}: ${buy.units} ${units} of ${item.sku}, ${fmtMoney(buy.baseValue)}, delivered in ${days(buy.leadDays)} — ${itemLink(item)}`;
  }
  return `${item.sku}: left open — ${itemLink(item)}`;
}

/** "one purchase order each to MRO Supply Co. ($1,478, two items on one delivery) and Graybar ($930)". */
function purchaseSummary(buys: DemandItem[]): string {
  const bySupplier = new Map<string, { total: number; items: number }>();
  for (const item of buys) {
    const buy = item.scenarios.buy;
    if (!buy) continue;
    const entry = bySupplier.get(buy.vendor) ?? { total: 0, items: 0 };
    entry.total += buy.baseValue;
    entry.items += 1;
    bySupplier.set(buy.vendor, entry);
  }
  const parts = [...bySupplier].map(
    ([vendor, { total, items }]) =>
      `${vendor} (${fmtMoney(total)}${items > 1 ? `, ${items} items on one delivery` : ''})`,
  );
  const count = parts.length;
  return `${count} purchase ${plural(count, 'order', 'orders')} to ${parts.join(' and ')}`;
}

function fulfillTurn(): ReplenishmentTurn {
  const rows = openOrders();
  if (rows.length === 0) {
    return reply('Everything in Open demand is already fulfilled.');
  }
  const transfers = rows.filter(isTransfer);
  const buys = rows.filter((item) => !isTransfer(item) && item.scenarios.buy);
  const purchaseTotal = buys.reduce(
    (sum, item) => sum + (item.scenarios.buy?.baseValue ?? 0),
    0,
  );
  const what: string[] = [];
  if (transfers.length > 0) {
    what.push(
      `${transfers.length} transfer ${plural(transfers.length, 'order', 'orders')} between our own warehouses`,
    );
  }
  if (buys.length > 0) what.push(purchaseSummary(buys));
  const limit = fmtMoney(APPROVAL_TIERS[0].limit);
  const plan = [
    `Here is what I would do with the ${rows.length} open ${plural(rows.length, 'item', 'items')}:`,
    rows.map((item) => `- ${fulfillPlanLine(item)}`).join('\n'),
    `That is ${what.join(' and ')}: ${fmtMoney(purchaseTotal)} of purchases, within the ${limit} buyer limit, so no further approval is needed. Nothing is sent to a warehouse or a supplier until you confirm. Go ahead?`,
  ].join('\n\n');
  const report = [
    `Done. Sent ${what.join(' and ')}:`,
    rows.map((item) => `- ${fulfillReportLine(item)}`).join('\n'),
    'Every row in Open demand now reads Fulfilled, and each item page shows what was sent.',
  ].join('\n\n');
  return {
    kind: 'propose',
    job: { plan, act: () => fulfillRows(rows), report },
  };
}

// --- Top up the carts short of a minimum -----------------------------------------------

interface ShortCart {
  cart: ResolvedCart;
  nudge: ThresholdNudge;
}

function shortCarts(): ShortCart[] {
  return cartsForMode(resolveBoard(getBuyerBoardState()), 'all').flatMap(
    (cart) => {
      const nudge = thresholdNudge(cart);
      return nudge === null ? [] : [{ cart, nudge }];
    },
  );
}

function cartLabel(cart: ResolvedCart): string {
  return `${getSupplier(cart.cart.supplierId).shortName} → ${getSite(cart.cart.shipToId).name}`;
}

function cartLink(cart: ResolvedCart): string {
  return `[${cartLabel(cart)}](${BOARD_PATH}?cart=${cart.cart.id})`;
}

function minimumName(nudge: ThresholdNudge): string {
  return nudge.constraint === 'mov'
    ? 'minimum order value'
    : 'free-freight minimum';
}

function topUpPlanLine({ cart, nudge }: ShortCart): string {
  return `**${cartLabel(cart)}** is ${fmtMoney(nudge.gap)} short of the ${fmtMoney(nudge.thresholdAmount)} ${minimumName(nudge)}: add ${fmtQty(nudge.addQty, nudge.uom)} of ${nudge.itemCode} (${fmtMoney(nudge.addCost)}), which lifts the PO from ${fmtMoney(cart.totals.poValue)} to ${fmtMoney(nudge.newTotal)} and covers ${nudge.coverageDays} more days of ${getSite(cart.cart.shipToId).name} usage.`;
}

function addTopUp({ cart, nudge }: ShortCart): void {
  const base = cart.lines.find((entry) => entry.line.id === nudge.baseLineId);
  if (base === undefined) return;
  addCartLine(
    makeForwardBuyLine({
      base: base.line,
      reason: 'mov',
      addedQty: nudge.addQty,
      coverageDays: nudge.coverageDays,
      unitCost: base.line.unitCost,
      costSource: base.line.costSource,
    }),
  );
}

function topUpTurn(): ReplenishmentTurn {
  const short = shortCarts();
  if (short.length === 0) {
    return reply('Every cart already clears its supplier minimums.');
  }
  const count = short.length;
  const plan = [
    `${count} ${plural(count, 'cart is', 'carts are')} short of a supplier minimum:`,
    short.map((entry) => `- ${topUpPlanLine(entry)}`).join('\n'),
    `I would add that quantity to each cart as a buy-ahead line, marked as such, which you can undo from the cart. Nothing is ordered until you release the cart. Go ahead?`,
  ].join('\n\n');
  const report = [
    `Done. Added:`,
    short
      .map(
        ({ cart, nudge }) =>
          `- ${fmtQty(nudge.addQty, nudge.uom)} of ${nudge.itemCode} to ${cartLink(cart)}: ${fmtMoney(nudge.newTotal)}, clears the ${minimumName(nudge)}.`,
      )
      .join('\n'),
    'Each cart shows the buy-ahead line and an Undo buy ahead link.',
  ].join('\n\n');
  return {
    kind: 'propose',
    job: {
      plan,
      act: () => {
        for (const entry of short) addTopUp(entry);
      },
      report,
    },
  };
}

// --- An item: transfer or purchase -----------------------------------------------------

function transferJob(item: DemandItem): ReplenishmentJob {
  const { transfer, buy } = item.scenarios;
  if (!transfer) throw new Error(`${item.id} has no transfer option`);
  const units = unitWord(item.uom);
  const otherOption = buy
    ? ` Buying from ${buy.vendor} instead is ${fmtMoney(buy.baseValue)} plus ${fmtMoney(buy.freightIfBelow)} freight (under its ${fmtMoney(buy.freeFreightThreshold)} free-freight level) and takes ${days(buy.leadDays)}${buy.leadDays > item.needByDays ? ', too late' : ''}.`
    : ` A supplier order takes ${days(transfer.vendorLeadDays)}${transfer.vendorLeadDays > item.needByDays ? ' and would miss it' : ''}.`;
  const shown = buy
    ? ' I have put the transfer next to the purchase under Suggested options so you can compare.'
    : '';
  const plan = `${buy ? 'Yes.' : 'A transfer is the plan here.'} ${transfer.fromWarehouse} has ${transfer.surplusUnits} on hand, ${transfer.overMaxUnits} over its maximum. I would move ${transfer.transferUnits} ${units} to ${transfer.toWarehouse}: arrives in ${days(transfer.etaDays)}; ${needText(item)}. No purchase cost and no freight, since a truck already runs this lane.${otherOption}${shown} Shall I send the transfer order to ${transfer.fromWarehouse}? Nothing moves until you say go ahead.`;
  return {
    plan,
    preview: buy ? () => showAlternative(item.id) : undefined,
    act: () => {
      if (buy) showAlternative(item.id);
      setFulfillStatus(item.id, 'fulfilled', 'transfer');
    },
    report: `Done. Transfer order sent to ${transfer.fromWarehouse}: ${transfer.transferUnits} ${units} of ${item.sku} to ${transfer.toWarehouse}, arriving in ${days(transfer.etaDays)}. This item now reads Transfer order sent, and its row in [Open demand](${BOARD_PATH}) reads Fulfilled.`,
  };
}

function itemTurn(item: DemandItem): ReplenishmentTurn {
  const { transfer, buy } = item.scenarios;
  const units = unitWord(item.uom);
  if (getFulfillStatus(item.id) === 'fulfilled') {
    return reply(
      transfer && isTransfer(item)
        ? `The transfer order is already sent: ${transfer.transferUnits} ${units} from ${transfer.fromWarehouse} arrive in ${days(transfer.etaDays)}.`
        : `The order for this item is already sent${buy ? ` to ${buy.vendor}` : ''}.`,
    );
  }
  if (transfer) return { kind: 'propose', job: transferJob(item) };
  const noTransfer = `No. None of the other warehouses has spare ${item.sku}, so a transfer is not an option.`;
  if (!buy) return reply(noTransfer);
  if (item.sourcing) {
    return reply(
      `${noTransfer} ${item.sourcing.vendors.length} distributors stock it; compare their prices with Negotiate across suppliers on this page before buying ${buy.units} ${units}.`,
    );
  }
  return {
    kind: 'propose',
    job: {
      plan: `${noTransfer} The plan is to buy ${buy.units} ${units} from ${buy.vendor} at ${unitPrice(buy.unitCost, item.uom)}, ${fmtMoney(buy.baseValue)}, delivered in ${days(buy.leadDays)}; ${needText(item)}. Shall I send the purchase order? Nothing is sent until you say go ahead.`,
      act: () => setFulfillStatus(item.id, 'fulfilled', 'buy'),
      report: `Done. Purchase order sent to ${buy.vendor}: ${buy.units} ${units} of ${item.sku}, ${fmtMoney(buy.baseValue)}, delivered in ${days(buy.leadDays)}. Its row in [Open demand](${BOARD_PATH}) reads Fulfilled.`,
    },
  };
}

// --- Routing ---------------------------------------------------------------------------

/**
 * Reads a replenishment page address (`/procurement/replenishment/board`): the
 * page itself has no item, an item page has its id. Any other address is not a
 * replenishment page.
 */
function replenishmentPage(pathname: string): { itemId?: string } | undefined {
  const [area, section, board, itemId, ...rest] = pathname
    .split('/')
    .filter((segment) => segment.length > 0);
  if (
    area !== 'procurement' ||
    section !== 'replenishment' ||
    board !== 'board' ||
    rest.length > 0
  ) {
    return undefined;
  }
  return { itemId };
}

/**
 * What Nami does for a message on a replenishment page, or undefined when the
 * message is not about this page's jobs (or the page is not a replenishment one).
 * `proposed` is the job Nami last asked about, so "Go ahead" runs it.
 */
export function planReplenishment(
  message: string,
  pathname: string,
  proposed: ReplenishmentJob | null = null,
): ReplenishmentTurn | undefined {
  const page = replenishmentPage(pathname);
  if (!page) return undefined;
  if (isResetCommand(message)) {
    return reply(
      'Done. Every item in Open demand is back to Open, and the item pages show their original options.',
      () => {
        resetFulfillStatuses();
        hideAlternatives();
      },
    );
  }
  if (proposed !== null) {
    if (GO_AHEAD.test(message)) return { kind: 'run', job: proposed };
    if (DECLINE.test(message)) return reply('Understood. Nothing has changed.');
  }
  if (page.itemId === undefined) {
    if (TOP_UP_REQUEST.test(message)) return topUpTurn();
    return FULFILL_REQUEST.test(message) ? fulfillTurn() : undefined;
  }
  const item = getItem(page.itemId);
  if (!item || !TRANSFER_REQUEST.test(message)) return undefined;
  return itemTurn(item);
}

/** The prompts to offer on a replenishment page, or undefined when it has none. */
export function replenishmentSuggestions(
  pathname: string,
): string[] | undefined {
  const page = replenishmentPage(pathname);
  if (!page) return undefined;
  if (page.itemId === undefined) return [FULFILL_ALL_ORDERS, TOP_UP_MINIMUMS];
  const item = getItem(page.itemId);
  if (!item?.scenarios.transfer) return undefined;
  return [item.scenarios.buy ? SHOW_TRANSFER_OPTION : CREATE_TRANSFER_ORDER];
}
